import { ReplenishInvites } from "@/modals";
import { useGetUser, useGetUserInvites } from "@/queries";
import { useUIStore } from "@/stores";
import {
  formatDate,
  formatInstProfile, formatUserName,
  formatUserRoleToName,
  ImgSrc
} from "@/utils";
import { Instagram } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { purple } from "@mui/material/colors";
import { FC } from "react";
import { Loading } from "react-admin";
import { useParams } from "react-router-dom";

const ShortInfoPanel: FC = () => {
  const { id } = useParams();
  const { openModal } = useUIStore();

  const { user } = useGetUser(id ?? "");
  const { freeInvites, freeInvitesError } = useGetUserInvites(id ?? "");

  if (!user) {
    return <Loading />;
  }

  return (
    <Grid spacing={4} item xs={12}>
      <Stack
        direction="row"
        width="100%"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box display="flex" gap={2}>
          <Avatar
            src={ImgSrc(user.profileImage?.path ?? "")}
            alt="Profile Image"
            sx={{ width: 56, height: 56 }}
          />
          <Stack direction={"column"}>
            <Stack direction={"row"} alignItems={"center"} gap={2}>
              <Typography variant="h6" fontWeight="bold">
                {formatUserName(user?.firstName, user?.lastName, user.status)}
              </Typography>
              {user.socialLinks?.instagram && (
                <Stack direction={"row"} alignItems={"center"} columnGap={0.5}>
                  <Instagram fontSize="small" sx={{ color: purple[200] }} />
                  <Link
                    sx={{ color: purple[200], textDecoration: "none" }}
                    href={user.socialLinks.instagram}
                    target={"_blank"}
                  >
                    {formatInstProfile(user.socialLinks.instagram)}
                  </Link>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <Divider sx={{ margin: "32px 0" }} />

      <Grid container spacing={3}>
        {/* LEFT SIDE - user info table */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="gray">
                Role
              </Typography>
              <Typography variant="subtitle2">
                {formatUserRoleToName(user.role)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="gray">
                Date of Approval
              </Typography>
              <Typography variant="subtitle2">
                {user.completedOnboardingAt
                  ? formatDate(user.completedOnboardingAt)
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="gray">
                Date of Application
              </Typography>
              <Typography variant="subtitle2">
                {formatDate(user.createdAt)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="gray">
                Email Address
              </Typography>
              <Typography variant="subtitle2">{user.email}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="gray">
                Phone Number
              </Typography>
              <Typography variant="subtitle2">{user.phoneNumber}</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* RIGHT SIDE - Invite Balance */}
        <Grid item xs={6} md={3}>
          <Typography variant="body2" color="gray">
            Invite Balance
          </Typography>
          <Stack direction="row" spacing={2} mb={2}>
            <Typography>
              Used: <strong>{freeInvitesError ? "—" : freeInvites}</strong>
            </Typography>
            <Typography>
              Left: <strong>{user.remainingInvitesCount}</strong>
            </Typography>
          </Stack>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() =>
              openModal(ReplenishInvites, {
                title: "Replenish Invites",
                userId: id ?? "",
              })
            }
          >
            Replenish Invites
          </Button>
        </Grid>
      </Grid>

      {!!user.referralRequests?.length && (
        <Stack mt={1}>
          <Typography variant="body2" color="gray">
            Approved referrals
          </Typography>
          <Stack direction="row" spacing={1} mt={0.5}>
            {user.referralRequests.map(
              ({ id, referrer: { firstName, lastName } }) => (
                <Chip
                  key={id}
                  size="small"
                  variant="outlined"
                  color={"default"}
                  label={`${firstName} ${lastName}`}
                />
              )
            )}
          </Stack>
        </Stack>
      )}

      {user.vettingNote && (
        <Box mt={4}>
          <Typography variant="body2" color="gray">
            Vetting Note
          </Typography>
          <Box mt={1} p={2} borderRadius={2} sx={{ border: "1px solid #333" }}>
            <Typography variant="body2">{user.vettingNote}</Typography>
          </Box>
        </Box>
      )}
    </Grid>
  );
};

export { ShortInfoPanel };
